/**
 * Новая цель — один лист для настройки и для приложения (22.10.2026, макет
 * docs/design/forms-2026-10-22).
 *
 * Прежде в настройке была старая форма: «Начать копить», «Без срока» как
 * очистка даты, важность «сначала это / важно / обычная / когда получится» и
 * сохранение без взноса и даты. В приложении — «Где копим», «Как копим»,
 * «Что за цель», но без «Начать копить», и важность другими словами. Теперь
 * лист один: вид — переключатель сверху, сумма крупно, «Как копим» своим
 * блоком; важность — словами приложения с пояснением из настройки.
 *
 * Цель без взноса и без даты не сохраняется нигде (решение владельца
 * 22.10.2026): она не говорит, когда соберётся.
 */
import { useState } from 'react';
import { ZERO, type Account, type GoalPriority, type PlainDate } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { BigAmount, CardAccount, CardInput, CardMoney, CardPick, Segs } from './card-fields.js';
import { GoalTerms, PRIORITY_OPTIONS, goalPlan, termsMissing } from './goal-edit.js';
export { PRIORITY_OPTIONS };
import { formatMoney } from '@skoka/core';
import { parseAmountInput, todayPd } from './store.js';

export type GoalKindD = 'emergency_fund' | 'purchase' | 'sinking_fund' | 'debt_payoff';

/** Цель в черновике. */
export interface GoalDraft {
  name: string;
  target: string;
  current: string;
  monthly: string;
  /** К какой дате; пусто — без срока. */
  date: string;
  kind: GoalKindD;
  /** С какого дня начинаем откладывать. Пусто — с ближайшего прихода. */
  start: string;
  priority: GoalPriority;
  /**
   * Долг, который цель закрывает досрочно, — черновой ссылкой `card:0`
   * или `loan:1` (старые черновики настройки).
   */
  debtRef?: string;
  /** Где копим: счёт; в настройке — черновой ссылкой `a2`. */
  accountId?: string;
  /** «Без срока» включён. Нет — по дате: пустая дата и есть «без срока». */
  noDate?: boolean;
}

export const newGoal = (
  name: string, kind: GoalKindD, target = '', priority: GoalPriority = 'medium', debtRef?: string,
): GoalDraft => ({
  name, target, current: '', monthly: '', date: '', kind, start: '', priority,
  ...(debtRef ? { debtRef } : {}),
});


const KINDS = [['purchase', 'Покупка'], ['emergency_fund', 'Подушка'], ['sinking_fund', 'Под платёж']] as const;
/** Имя по виду и важность, пока человек не назвал свои. */
const KIND_DEFAULT: Record<GoalKindD, { name: string; priority: GoalPriority }> = {
  purchase: { name: '', priority: 'medium' },
  emergency_fund: { name: 'Подушка', priority: 'high' },
  sinking_fund: { name: 'Резерв под платёж', priority: 'high' },
  debt_payoff: { name: '', priority: 'high' },
};

const isDate = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);

export function GoalSheet({
  init, isNew, accounts, hasBuffer, buffer, startText, startSub, onSave, onDelete, onClose,
}: {
  init: GoalDraft;
  isNew: boolean;
  /** «Где копим». Нет счетов — строки нет. */
  accounts?: Account[];
  /** Подушка уже есть: второй не предлагаем — движки видят только первую. */
  hasBuffer: boolean;
  /** Подушка: три месяца обязательных трат — сумма и как она посчитана. */
  buffer?: { target: string; text: string };
  /** «Начать копить» по умолчанию: «с ближайшей зарплаты» и её дата. */
  startText: string;
  startSub?: string;
  onSave: (v: GoalDraft) => void | Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const today = todayPd();
  const [v, setV] = useState<GoalDraft>({ ...init, noDate: init.noDate ?? !isDate(init.date) });
  const [busy, setBusy] = useState(false);
  /** Важность, названная человеком; до того — по виду цели. */
  const [chosen, setChosen] = useState(!isNew);
  const on = <K extends keyof GoalDraft>(k: K, x: GoalDraft[K]) => setV((p) => ({ ...p, [k]: x }));
  const noDate = v.noDate ?? true;
  const when = noDate ? '' : v.date;
  const kinds = KINDS.filter(([k]) => !(k === 'emergency_fund' && hasBuffer && init.kind !== 'emergency_fund'));
  const t = parseAmountInput(v.target);
  const plan = goalPlan({
    id: 'new', name: v.name, kind: v.kind === 'debt_payoff' ? 'purchase' : v.kind, priority: v.priority,
    targetAmount: t, currentAmount: parseAmountInput(v.current),
    monthlyContribution: parseAmountInput(v.monthly), contributionMode: 'fixed', fundedInPeriod: false, status: 'active',
    ...(isDate(v.start) ? { startDate: v.start as PlainDate } : {}),
  }, v.monthly, when, today);
  const goalAccount = accounts?.find((a) => a.id === v.accountId);

  const setKind = (k: GoalKindD) => setV((p) => {
    const was = KIND_DEFAULT[p.kind];
    const now = KIND_DEFAULT[k];
    return {
      ...p, kind: k,
      ...(p.name.trim() === '' || p.name === was.name ? { name: now.name } : {}),
      ...(chosen ? {} : { priority: now.priority }),
      // Подушка — три месяца обязательных трат, если сумму ещё не назвали.
      ...(k === 'emergency_fund' && buffer && parseAmountInput(p.target) === ZERO ? { target: buffer.target } : {}),
    };
  });

  const ready = !busy && v.name.trim() !== '' && t > ZERO && !termsMissing(plan);

  return (
    <SheetWrap title={isNew ? 'Новая цель' : v.name || 'Цель'} onClose={onClose} className="rcpt"
      {...(v.debtRef ? {
        tip: 'Это досрочное гашение долга. Ежемесячный платёж по нему уже стоит в обязательствах '
          + 'и вычитается отдельно — здесь только то, что сверх него.',
      } : {})}>
      <div className="rcform">
        {v.kind !== 'debt_payoff' && (
          <Segs label="Что за цель" options={kinds} value={v.kind} onChange={setKind} />
        )}
        <BigAmount label="Сколько нужно" value={v.target} onChange={(x) => on('target', x)}
          {...(v.kind === 'emergency_fund'
            ? { tip: 'Подушка — запас на чёрный день: SKOKA наполняет её раньше других целей и предупреждает, если покупка её съест.' }
            : v.kind === 'sinking_fund'
              ? { tip: 'Резерв под платёж — деньги на известный будущий счёт: страховку, налог, ремонт машины. Взнос в него считается обязательным, как платёж.' }
              : {})} />
        {v.kind === 'emergency_fund' && buffer && (
          <div className="rcchips"><span className="rcinfo">{buffer.text}</span></div>
        )}

        <div className="rccard rcsel">
          <CardInput label="Название" value={v.name} onChange={(x) => on('name', x)} placeholder="Отпуск" width={170} />
          <CardMoney label="Уже накоплено" value={v.current} onChange={(x) => on('current', x)}
            tip="Только то, что уже лежит на твоих счетах под эту цель: новых денег эта строка не создаёт." />
          {accounts && accounts.length > 0 && (
            <CardAccount label="Где копим" pickerTitle="Где копим" accounts={accounts}
              value={v.accountId ?? ''} onChange={(id) => on('accountId', id)} />
          )}
        </div>

        <GoalTerms plan={plan} today={today} monthly={v.monthly} onMonthly={(x) => on('monthly', x)}
          date={v.date} onDate={(x) => on('date', x)} noDate={noDate} onNoDate={(x) => on('noDate', x)}
          start={{ value: v.start, onChange: (x) => on('start', x), text: startText, ...(startSub ? { sub: startSub } : {}) }} />

        <div className="rccard rcsel">
          <CardPick label="Насколько важно" value={v.priority} options={PRIORITY_OPTIONS}
            sub={PRIORITY_OPTIONS.find((o) => o.id === v.priority)?.sub ?? ''}
            tip="Важность решает, кто получит деньги первым, когда их на всех не хватает. Вид цели только предлагает начальное значение — выбор твой."
            onChange={(x) => { setChosen(true); on('priority', x as GoalPriority); }} />
        </div>
        {/* Накопленное на карте для трат закрепляется под цель — иначе те же
            деньги посчитаются и в «Можно тратить». */}
        {parseAmountInput(v.current) > ZERO && goalAccount?.isLiquid && (
          <p className="note">
            {formatMoney(parseAmountInput(v.current))} на «{goalAccount.name}» закрепим под цель — в «Можно тратить» они не попадут.
          </p>
        )}

        <button className="btn pri" disabled={!ready}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({ ...v, name: v.name.trim(), date: noDate ? '' : v.date, monthly: v.monthly });
            } finally { setBusy(false); }
          }}>{isNew ? 'Добавить' : 'Сохранить'}</button>
        {onDelete && !isNew && (
          <button className="textact danger" disabled={busy}
            onClick={() => { if (confirm('Удалить цель?')) onDelete(); }}>Удалить</button>
        )}
      </div>
    </SheetWrap>
  );
}
